import type {
  SecondaryMarketKeys,
  SecondaryMarketKeysPrefix,
  SecondaryMarketKeysSuffix,
} from 'locales/secondaryMarketKeys';
import type { FormError, FormErrorSeverity, FormRecord } from 'models/formError';
import type { LimitOrderForm } from 'state/secondaryMarket/limit-order/form/input/inputForm.model';
import type { Form } from 'state/secondaryMarket/shared/form/form.model';

export type SecondaryMarketFormError<F = Form> = FormError<F, SecondaryMarketKeys, SecondaryMarketKeysPrefix>;

export const mandatoryFieldsErrors = [
  'quantityMissing',
  'missingIsin',
  'missingSales',
  'missingSalesTeam',
  'missingCashAmountOrQuantity',
  'missingValuationDate',
  'missingSettlementDate',
  'missingCustodian',
  'missingSettlementType',
  'missingTradeWay',
  'limitMissing',
  'limitDateMissing',
  'limitTimeMissing',
];

export const MISSING_ISIN_ERROR: SecondaryMarketFormError = {
  translationKey: 'missingIsin',
  fields: [],
  severity: 'Error',
  isDisplayedInSummary: true,
  values: {},
};

export const DUPLICATE_CUSTODIANS_ERROR: SecondaryMarketFormError<Form> = {
  translationKey: 'duplicateCustodians',
  fields: ['custodian'],
  severity: 'Error',
  values: {},
};
export const MISSING_UNALLOCATED_NOTIONAL_ERROR: SecondaryMarketFormError<FormRecord> = {
  translationKey: 'unallocatedNotionalMissing',
  fields: ['unallocatedNotional'],
  severity: 'Error',
  values: {},
};
export const MISSING_NOTIONAL_ERROR: SecondaryMarketFormError<FormRecord> = {
  translationKey: 'notionalMissing',
  fields: ['notional'],
  severity: 'Error',
  values: {},
};
export const MISSING_QUANTITY_ERROR: SecondaryMarketFormError<FormRecord> = {
  translationKey: 'quantityMissing',
  fields: ['quantity'],
  severity: 'Error',
  values: {},
};
export const MISSING_MANDATORY_QUANTITY_ERROR: SecondaryMarketFormError<FormRecord> = {
  translationKey: 'quantityMissing',
  fields: ['quantity'],
  severity: 'Error',
  values: {},
  isMandatory: true,
};

type Range = { min: number; max: number };

export const QUANTITY_NOT_IN_RANGE: (range: Range) => SecondaryMarketFormError<FormRecord> = ({ min, max }) => ({
  translationKey: 'quantityNotInRange',
  fields: ['quantity'],
  severity: 'Error',
  values: { min, max },
  isMandatory: true,
});

export const MISSING_LIMIT_ERROR: SecondaryMarketFormError<FormRecord> = {
  translationKey: 'limitMissing',
  fields: ['limit'],
  severity: 'Error',
  values: {},
  isMandatory: true,
};
export const MISSING_LIMIT_DATE_ERROR: SecondaryMarketFormError<FormRecord> = {
  translationKey: 'limitDateMissing',
  fields: ['limitDate'],
  severity: 'Error',
  values: {},
  isMandatory: true,
};
export const MISSING_LIMIT_TIME_ERROR: SecondaryMarketFormError<FormRecord> = {
  translationKey: 'limitTimeMissing',
  fields: ['limitTime'],
  severity: 'Error',
  values: {},
  isMandatory: true,
};
export const INTERNAL_INACTIVE_PRODUCT_ERROR: SecondaryMarketFormError = {
  translationKey: 'productNotActive.internal',
  fields: [],
  severity: 'Error',
  isDisplayedInSummary: true,
  values: {},
};
export const EXTERNAL_INACTIVE_PRODUCT_ERROR: SecondaryMarketFormError = {
  translationKey: 'productNotActive.external',
  fields: [],
  severity: 'Error',
  isDisplayedInSummary: true,
  values: {},
};

function createValidationResult<F extends Form>(
  translationKey: SecondaryMarketKeysSuffix,
  fields: readonly (keyof F)[],
  isMandatory = false,
  isDisplayedInSummary = false,
  severity: FormErrorSeverity = 'Error',
): Readonly<SecondaryMarketFormError<F>> {
  return {
    severity,
    values: {},
    isDisplayedInSummary,
    isMandatory,
    translationKey,
    fields,
  } as const;
}

export const MISSING_CLIENT_LIMIT = createValidationResult<LimitOrderForm>('clientLimitMissing', ['limit'], true);

export const MISSING_CLIENT_ERROR = createValidationResult('clientMissing', ['client'], true);
export const MISSING_CUSTODIAN_ERROR = createValidationResult('missingCustodian', ['custodian'], true);
export const CLIENT_NOT_FOUND_BY_DOMINO_ERROR = createValidationResult('clientNotFoundByDomino', ['salesDesk']);
export const SALES_COVERAGE_LOAD_FAILURE = createValidationResult(
  'salesCoverageLoadFailed',
  ['salesDesk'],
  false,
  false,
  'Failure',
);
export const MISSING_ELIOT_CODE_ERROR = createValidationResult('missingClientEliotCode', ['client']);
export const NO_SALES_FOR_CLIENT_ERROR = createValidationResult('noSalesForClient', ['salesDesk'], true);
export const MISSING_SALES_DESK_ERROR = createValidationResult('missingSalesDesk', ['salesDesk'], true);
export const MISSING_SALES_ERROR = createValidationResult('missingSales', ['salesId'], true);
export const MISSING_CASH_AMOUNT_OR_QUANTITY_ERROR = createValidationResult(
  'missingCashAmountOrQuantity',
  ['cashAmount', 'quantity'],
  true,
);

export const QUANTITY_NOT_DIVISIBLE_BY_LOT_SIZE = createValidationResult(
  'quantityNotDivisible',
  ['quantity'],
  true,
  false,
  'Warning',
);
export const MISSING_SETTLEMENT_DATE_ERROR = createValidationResult('missingSettlementDate', ['settlementDate'], true);
export const MISSING_SETTLEMENT_TYPE_ERROR = createValidationResult('missingSettlementType', ['settlementType'], true);
export const MISSING_TRADE_WAY_ERROR = createValidationResult('missingTradeWay', ['tradeWay'], true);
export const NO_MODIFIED_FIELDS_WARNING = createValidationResult('hasNoModifiedFields', [], false, true, 'Warning');
export const MANDATORY_FIELDS_WARNING = createValidationResult('mandatoryFields', [], false, true, 'Warning');
